<template>
  <div
    v-if="showBanner"
    class="cookie-banner fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-300 shadow-2xl shadow-black/40 drop-shadow-2xl transition-transform duration-300 ease-in-out"
    :class="{
      'transform translate-y-0': showBanner,
      'transform translate-y-full': !showBanner,
    }"
    role="region"
    aria-label="Aviso de Política de Cookies"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <!-- Estado padrão: aceitar / rejeitar / personalizar -->
      <div
        v-if="!showCustomize"
        class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            Nossa Declaração de Privacidade e Política de Cookies
          </h3>
          <p class="text-sm text-gray-600 leading-relaxed">
            Todos os sites da Kali Sistemas usam cookies para melhorar sua
            experiência on-line. Eles foram colocados no seu computador quando
            você acessou este site. Você pode escolher quais tipos aceitar.
          </p>
          <div class="mt-3 flex flex-wrap gap-4">
            <RouterLink
              to="/politica-de-privacidade"
              @click="trackPolicyView('privacy')"
              class="text-kali-blue-600 hover:text-kali-blue-700 text-sm font-medium underline"
            >
              Declaração de Privacidade
            </RouterLink>
            <RouterLink
              to="/politica-de-cookies"
              @click="trackPolicyView('cookies')"
              class="text-kali-blue-600 hover:text-kali-blue-700 text-sm font-medium underline"
            >
              Política de Cookies
            </RouterLink>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-3 min-w-fit">
          <button
            @click="rejectCookies"
            class="px-6 py-2 bg-white text-gray-600 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-kali-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Rejeitar
          </button>
          <button
            @click="openCustomize"
            class="px-6 py-2 bg-white text-kali-blue-700 text-sm font-medium rounded-md border border-kali-blue-200 hover:bg-kali-blue-50 focus:outline-none focus:ring-2 focus:ring-kali-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Personalizar
          </button>
          <button
            @click="acceptCookies"
            class="px-6 py-2 bg-kali-blue-600 text-white text-sm font-medium rounded-md hover:bg-kali-blue-700 focus:outline-none focus:ring-2 focus:ring-kali-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Aceitar todos
          </button>
          <button
            @click="closeBanner"
            class="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-kali-blue-500 focus:ring-offset-2 rounded-md transition-colors duration-200"
            aria-label="Fechar banner de cookies"
          >
            <svg
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Estado personalizar: uma categoria por vez -->
      <div v-else>
        <h3 class="text-lg font-semibold text-gray-900 mb-1">
          Personalizar cookies
        </h3>
        <p class="text-sm text-gray-500 mb-4">
          Necessários ficam sempre ativos — sem eles o site não funciona. Os
          outros você escolhe.
        </p>

        <div class="space-y-1 mb-4 max-w-2xl">
          <div class="flex items-center justify-between gap-4 py-3 border-b border-gray-100">
            <div>
              <p class="text-sm font-medium text-gray-900">Necessários</p>
              <p class="text-xs text-gray-500">
                Essenciais para o site funcionar (ex.: manter sua sessão).
              </p>
            </div>
            <span class="text-xs font-semibold text-gray-400 shrink-0">Sempre ativo</span>
          </div>

          <label class="flex items-center justify-between gap-4 py-3 border-b border-gray-100 cursor-pointer">
            <div>
              <p class="text-sm font-medium text-gray-900">Funcionalidade</p>
              <p class="text-xs text-gray-500">
                Lembra suas preferências de navegação no site.
              </p>
            </div>
            <input type="checkbox" v-model="prefs.functional" class="h-5 w-5 shrink-0 text-kali-blue-500 focus:ring-kali-blue-500" />
          </label>

          <label class="flex items-center justify-between gap-4 py-3 cursor-pointer">
            <div>
              <p class="text-sm font-medium text-gray-900">Analytics</p>
              <p class="text-xs text-gray-500">
                Google Analytics — nos ajuda a entender como o site é usado.
              </p>
            </div>
            <input type="checkbox" v-model="prefs.analytics" class="h-5 w-5 shrink-0 text-kali-blue-500 focus:ring-kali-blue-500" />
          </label>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <button
            @click="showCustomize = false"
            class="px-6 py-2 text-gray-500 text-sm font-medium hover:text-gray-700"
          >
            ← Voltar
          </button>
          <button
            @click="saveCustomPreferences"
            class="px-6 py-2 bg-kali-blue-600 text-white text-sm font-medium rounded-md hover:bg-kali-blue-700 focus:outline-none focus:ring-2 focus:ring-kali-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Salvar preferências
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "CookieBanner",
  data() {
    return {
      showBanner: false,
      showCustomize: false,
      prefs: { functional: false, analytics: false },
    };
  },

  mounted() {
    // Função utilitária para resetar consentimento durante testes / "Gerenciar
    // Cookies" — registrada primeiro, antes de qualquer return antecipado, pra
    // nunca ficar indisponível independente do caminho que o mounted() segue.
    window.kaliResetCookieConsent = () => {
      try {
        localStorage.removeItem("kali-cookie-consent");
        sessionStorage.removeItem("kali-banner-closed");
        this.showCustomize = false;
        this.showBanner = true;
        window.kaliCookieBannerVisible = true;
        window.dispatchEvent(new CustomEvent("banner-opened"));
        console.debug("[CookieBanner] Consentimento resetado e banner exibido");
      } catch (e) {
        console.warn("[CookieBanner] Falha ao resetar consentimento:", e);
      }
    };

    // Permite forçar exibição via query param para testes
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("show_cookie_banner") === "1") {
      this.showBanner = true;
      // Mesmo sinal que o fluxo normal emite ao abrir — sem isso, outros
      // componentes (ex.: ChatAssistant) não sabem que o banner está aberto
      // e podem aparecer por cima dele. O setTimeout(0) é necessário: sem
      // ele, o evento dispara de forma síncrona dentro do mounted() deste
      // componente, antes de componentes irmãos (ex.: ChatAssistant) que
      // ainda não montaram terem a chance de se inscrever no listener —
      // uma corrida de montagem que faz o evento passar batido.
      setTimeout(() => {
        window.kaliCookieBannerVisible = true;
        window.dispatchEvent(new CustomEvent("banner-opened"));
      }, 0);
      console.debug(
        "[CookieBanner] Forçado via query param show_cookie_banner=1",
      );
      return;
    }

    // Verifica se o usuário já aceitou os cookies permanentemente
    const cookieConsent = localStorage.getItem("kali-cookie-consent");
    // Verifica se o usuário fechou o banner nesta sessão
    const bannerClosed = sessionStorage.getItem("kali-banner-closed");

    // Em modo desenvolvimento, ignorar o flag de banner fechado na sessão para facilitar testes
    const ignoreBannerClosedInDev = import.meta.env.DEV === true;

    // Só mostra o banner se ainda não houve decisão explícita (aceitar,
    // rejeitar ou personalizar) E (não fechou nesta sessão OU estamos em
    // dev). Decisões explícitas são persistentes — não somem com storage de
    // sessão, diferente de só fechar o banner sem decidir.
    if (
      cookieConsent !== "accepted" &&
      cookieConsent !== "rejected" &&
      cookieConsent !== "custom" &&
      (!bannerClosed || ignoreBannerClosedInDev)
    ) {
      // Mostra o banner após um pequeno delay para melhor UX
      setTimeout(() => {
        this.showBanner = true;
        window.kaliCookieBannerVisible = true;
        window.dispatchEvent(new CustomEvent("banner-opened"));
        console.debug(
          "[CookieBanner] Exibido (consentimento ausente, bannerClosed:",
          bannerClosed,
          ", dev:",
          ignoreBannerClosedInDev,
          ")",
        );
      }, 1000);
    } else {
      console.debug(
        "[CookieBanner] Não exibido (consent:",
        cookieConsent,
        ", bannerClosed:",
        bannerClosed,
        ", dev:",
        ignoreBannerClosedInDev,
        ")",
      );
      window.kaliCookieBannerVisible = false;
    }
  },
  methods: {
    acceptCookies() {
      // Salva o consentimento no localStorage
      localStorage.setItem("kali-cookie-consent", "accepted");
      localStorage.setItem(
        "kali-cookie-consent-date",
        new Date().toISOString(),
      );
      localStorage.setItem("kali-cookie-consent-version", "2024-11-v1");
      localStorage.setItem(
        "kali-cookie-consent-categories",
        JSON.stringify({ functional: true, analytics: true }),
      );

      // Define cookies de sessão se necessário
      this.setCookie("kali-session", "active", 365);
      this.setCookie("kali-preferences", "default", 365);

      // Esconde o banner
      this.showBanner = false;
      window.kaliCookieBannerVisible = false;

      this.registerConsent(true, "Aceitou todos os cookies", "cookies_functional");
      this.registerConsent(true, "Aceitou todos os cookies", "cookies_analytics");

      // Emite evento para componentes pai se necessário
      this.$emit("cookies-accepted");

      // Emite evento global para outros componentes — só esse evento libera o GTM.
      window.dispatchEvent(new CustomEvent("cookies-accepted"));
    },

    // Opt-out real: persiste a recusa (banner não volta a aparecer) e
    // garante que analytics nunca carrega — diferente de só fechar sem decidir.
    rejectCookies() {
      localStorage.setItem("kali-cookie-consent", "rejected");
      localStorage.setItem(
        "kali-cookie-consent-date",
        new Date().toISOString(),
      );
      localStorage.setItem("kali-cookie-consent-version", "2024-11-v1");
      localStorage.setItem(
        "kali-cookie-consent-categories",
        JSON.stringify({ functional: false, analytics: false }),
      );

      // Se a pessoa tinha aceitado antes e agora está revogando, apaga de
      // verdade os cookies que só existem por causa daquele aceite anterior
      // — "Gerenciar Cookies" precisa desfazer o que já foi feito, não só
      // parar de fazer coisas novas.
      this.deleteCookie("kali-session");
      this.deleteCookie("kali-preferences");

      this.showBanner = false;
      window.kaliCookieBannerVisible = false;

      this.registerConsent(false, "Rejeitou todos os cookies", "cookies_functional");
      this.registerConsent(false, "Rejeitou todos os cookies", "cookies_analytics");

      window.dispatchEvent(new CustomEvent("cookies-rejected"));
    },

    // Abre o painel de personalização, já pré-marcado com a última escolha salva.
    openCustomize() {
      try {
        const stored = JSON.parse(
          localStorage.getItem("kali-cookie-consent-categories") || "null",
        );
        if (stored) {
          this.prefs.functional = !!stored.functional;
          this.prefs.analytics = !!stored.analytics;
        }
      } catch {
        // ignora storage corrompido — mantém os padrões
      }
      this.showCustomize = true;
    },

    saveCustomPreferences() {
      localStorage.setItem("kali-cookie-consent", "custom");
      localStorage.setItem(
        "kali-cookie-consent-date",
        new Date().toISOString(),
      );
      localStorage.setItem("kali-cookie-consent-version", "2024-11-v1");
      localStorage.setItem(
        "kali-cookie-consent-categories",
        JSON.stringify(this.prefs),
      );

      if (this.prefs.functional) {
        this.setCookie("kali-preferences", "default", 365);
      } else {
        this.deleteCookie("kali-preferences");
      }
      // kali-session é necessário — sempre ativo, independente da escolha.
      this.setCookie("kali-session", "active", 365);

      this.showBanner = false;
      this.showCustomize = false;
      window.kaliCookieBannerVisible = false;

      this.registerConsent(
        this.prefs.functional,
        `Personalizou cookies — funcionalidade: ${this.prefs.functional ? "sim" : "não"}`,
        "cookies_functional",
      );
      this.registerConsent(
        this.prefs.analytics,
        `Personalizou cookies — analytics: ${this.prefs.analytics ? "sim" : "não"}`,
        "cookies_analytics",
      );

      if (this.prefs.analytics) {
        this.$emit("cookies-accepted");
        window.dispatchEvent(new CustomEvent("cookies-accepted"));
      } else {
        window.dispatchEvent(new CustomEvent("cookies-rejected"));
      }
    },

    closeBanner() {
      // Esconde o banner e salva que o usuário fechou na sessão atual, sem
      // registrar isso como aceite nem recusa — não decidiu, então nada de
      // analytics carrega, mas o banner pode voltar a aparecer depois.
      this.showBanner = false;
      window.kaliCookieBannerVisible = false;

      // Salva que o usuário fechou o banner (apenas para esta sessão)
      sessionStorage.setItem("kali-banner-closed", "true");

      this.registerConsent(false, "Fechou o banner sem decidir (botão X)");

      // Emite evento global para outros componentes (ex.: liberar o chat assistant)
      window.dispatchEvent(new CustomEvent("banner-closed"));
    },

    // Registra o consentimento (ou a recusa/dispensa) no backend, pra auditoria —
    // não bloqueia a UI: se a chamada falhar, o consentimento local já foi salvo.
    registerConsent(accepted, text, consentType = "cookies") {
      api
        .post("/public/consent", {
          consent_type: consentType,
          accepted,
          policy_version: "2024-11-v1",
          consent_text: text,
          source: "cookie_banner",
          page_url: window.location.href,
        })
        .catch(() => {});
    },

    setCookie(name, value, days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    },

    deleteCookie(name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`;
    },

    trackPolicyView(type) {
      try {
        const key =
          type === "cookies"
            ? "kali-policy-cookies-viewed"
            : "kali-policy-privacy-viewed";
        localStorage.setItem(key, new Date().toISOString());
        window.dispatchEvent(
          new CustomEvent("policy-viewed", { detail: { type } }),
        );
      } catch (e) {
        console.warn(
          "[CookieBanner] Falha ao registrar visualização de política:",
          e,
        );
      }
    },
  },
};
</script>

<style scoped>
/* Animações personalizadas se necessário */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Garantir que o banner fique acima de outros elementos */
.z-50 {
  z-index: 50;
}

/* Responsividade adicional */
@media (max-width: 640px) {
  .max-w-7xl {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
